import { h } from "preact";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { createSupportApi } from "../support/api";

function EnvelopeIcon({ size = 40 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z" />
    </svg>
  );
}

/**
 * Badge visuel neutre pour l'icône support.
 *
 * Important avec MUI/Preact : placer ce composant A L'INTERIEUR d'IconButton,
 * jamais autour d'IconButton. Ainsi ButtonBase/useIsFocusVisible conserve sa
 * ref directement sur le vrai <button> DOM.
 *
 * Exemple :
 * <IconButton ...>
 *   <SupportIconeBadge>
 *     <EmailIcon />
 *   </SupportIconeBadge>
 * </IconButton>
 */
export function SupportIconeBadge(props) {
  const {
    apiBase = "/api/support",
    csrfToken = null,
    pollIntervalMs = 30000,
    size = 40,
    children,
    style,
    title = "Support",
    ...rootProps
  } = props;

  const api = useMemo(
    () => createSupportApi({ baseUrl: apiBase, csrfToken }),
    [apiBase, csrfToken]
  );

  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const count = await api.unreadCount();
      setUnreadCount(Math.max(0, Number(count) || 0));
    } catch (_error) {
      // Le badge ne doit jamais casser le header si le support est indisponible.
    }
  }, [api]);

  useEffect(() => {
    refresh();

    if (typeof window === "undefined") return undefined;

    const onFocus = () => refresh();
    const onUnreadChanged = () => refresh();

    window.addEventListener("focus", onFocus);
    window.addEventListener("support:unread-changed", onUnreadChanged);

    const timer =
      pollIntervalMs > 0 ? window.setInterval(refresh, pollIntervalMs) : null;

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("support:unread-changed", onUnreadChanged);
      if (timer) window.clearInterval(timer);
    };
  }, [pollIntervalMs, refresh]);

  const badgeText = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <span
      {...rootProps}
      title={title}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
        ...style,
      }}
    >
      {children || <EnvelopeIcon size={size} />}

      {unreadCount > 0 ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -5,
            right: -7,
            minWidth: 16,
            height: 16,
            padding: "0 4px",
            borderRadius: 8,
            boxSizing: "border-box",
            background: "#d32f2f",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: "16px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {badgeText}
        </span>
      ) : null}
    </span>
  );
}
